<template>
  <div class="page-container">
    <header class="header">
      <button type="button" class="back-btn" aria-label="返回" @click="goBack">
        <img src="@/assets/返回.svg" alt="返回" class="icon-back" />
      </button>
      <h1 class="title">基础数据</h1>
      <div class="header-right"></div>
    </header>

    <main class="content">
      <div class="search-panel">
        <el-form
          class="filter-form"
          label-width="72px"
          @submit.prevent="handleSearch"
        >
          <el-form-item label="编码" required>
            <el-input
              v-model="searchForm.materialNo"
              placeholder="请输入物料编码"
              clearable
              @keyup.enter="handleSearch"
            />
          </el-form-item>

          <el-form-item label="供应商">
            <el-select
              v-model="selectedSupplierId"
              placeholder="请选择供应商"
              filterable
              remote
              :remote-method="handleSupplierInput"
              :loading="supplierLoading"
              clearable
              style="width: 100%"
            >
              <el-option
                v-for="supplier in supplierOptions"
                :key="supplier.id"
                :label="supplier.supplierName"
                :value="supplier.id"
              />
            </el-select>
          </el-form-item>

          <el-form-item class="form-item-actions" label-width="0">
            <el-button
              type="primary"
              :loading="searchLoading"
              @click="handleSearch"
            >
              查询
            </el-button>
            <el-button :disabled="searchLoading" @click="handleReset">
              重置
            </el-button>
          </el-form-item>
        </el-form>
      </div>

      <div v-loading="searchLoading" class="result-panel">
        <div v-if="searchResults.length > 0" class="results-header">
          <span class="results-title">
            搜索结果
            <em>{{ searchResults.length }}</em>
            条
          </span>
        </div>

        <div v-if="searchResults.length > 0" class="material-list">
          <div
            v-for="item in searchResults"
            :key="item.id"
            class="material-card"
          >
            <div class="card-main">
              <div class="card-image-wrap">
                <el-image
                  v-if="formatImageUrl(item.image)"
                  :src="formatImageUrl(item.image)"
                  :preview-src-list="[formatImageUrl(item.image)]"
                  :initial-index="0"
                  fit="cover"
                  preview-teleported
                  class="card-image"
                >
                  <template #error>
                    <div class="card-image-empty">
                      <el-icon class="empty-icon"><Picture /></el-icon>
                    </div>
                  </template>
                </el-image>
                <div v-else class="card-image-empty">
                  <el-icon class="empty-icon"><Picture /></el-icon>
                </div>
              </div>

              <div class="card-info">
                <el-tag
                  v-if="item.supplierName"
                  size="small"
                  type="info"
                  effect="plain"
                  class="supplier-tag"
                >
                  {{ item.supplierName }}
                </el-tag>
                <div class="info-code">{{ item.materialNo || "-" }}</div>
                <div class="info-desc">{{ item.materialDesc || "-" }}</div>
              </div>
            </div>

            <div class="card-footer">
              <el-button type="primary" link @click="openImageModal(item)">
                <el-icon><Edit /></el-icon>
                {{ item.image ? "修改图片" : "上传图片" }}
              </el-button>
            </div>
          </div>
        </div>

        <el-empty
          v-else-if="searched && !searchLoading"
          description="未找到匹配的物料信息"
          :image-size="80"
        />
        <el-empty
          v-else-if="!searched && !searchLoading"
          description="请输入物料编码后点击查询"
          :image-size="80"
        />
      </div>
    </main>

    <el-dialog
      v-model="showImageModal"
      :title="dialogTitle"
      width="92%"
      style="max-width: 480px"
      :close-on-click-modal="false"
      append-to-body
      destroy-on-close
      @closed="onDialogClosed"
    >
      <div v-if="currentMaterial" class="image-modal-content">
        <div class="modal-material-info">
          <el-tag
            v-if="currentMaterial.supplierName"
            size="small"
            type="info"
            effect="plain"
          >
            {{ currentMaterial.supplierName }}
          </el-tag>
          <div class="modal-material-code">
            {{ currentMaterial.materialNo }}
          </div>
          <div class="modal-material-desc">
            {{ currentMaterial.materialDesc || "-" }}
          </div>
        </div>

        <div class="modal-section">
          <div class="section-label">
            {{ formatImageUrl(currentMaterial.image) ? "图片预览" : "暂无图片" }}
          </div>
          <div class="modal-image-box">
            <el-image
              v-if="formatImageUrl(currentMaterial.image)"
              :src="formatImageUrl(currentMaterial.image)"
              :preview-src-list="[formatImageUrl(currentMaterial.image)]"
              fit="contain"
              preview-teleported
              class="modal-preview-image"
            >
              <template #error>
                <div class="modal-image-empty">图片加载失败</div>
              </template>
            </el-image>
            <div v-else class="modal-image-empty">
              <el-icon class="empty-icon"><Picture /></el-icon>
              <span>上传后将在此预览</span>
            </div>
            <el-button
              v-if="formatImageUrl(currentMaterial.image)"
              type="danger"
              size="small"
              class="modal-remove-btn"
              @click="removeImage"
            >
              删除
            </el-button>
          </div>
        </div>

        <div class="modal-section">
          <div class="section-label">重新上传</div>
          <el-upload
            :http-request="handleOBSUpload"
            :on-success="handleUploadSuccess"
            :on-error="handleUploadError"
            :before-upload="beforeUpload"
            accept="image/*"
            class="modal-upload"
            :auto-upload="true"
            :show-file-list="false"
            :disabled="uploadLoading"
            drag
          >
            <el-icon class="upload-icon"><Plus /></el-icon>
            <div class="upload-text">
              {{ uploadLoading ? "上传中..." : "点击或拖拽上传图片" }}
            </div>
            <div class="upload-tip">支持 jpg / png / gif，不超过 5MB</div>
          </el-upload>
        </div>
      </div>

      <template #footer>
        <el-button @click="closeImageModal">取消</el-button>
        <el-button
          type="primary"
          :loading="saveLoading"
          :disabled="!imageChanged || uploadLoading"
          @click="handleSave"
        >
          {{ saveLoading ? "保存中..." : "保存" }}
        </el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { ref, reactive, computed } from "vue";
import { useRouter } from "vue-router";
import { ElMessage } from "element-plus";
import { Plus, Picture, Edit } from "@element-plus/icons-vue";
import { getSuppliers } from "@/api/stockIn";
import { searchMaterialByNo } from "@/api/stockTransfer";
import service from "@/utils/request";
import { getUicAuthUserInfo } from "@/utils/auth";
import OBS from "esdk-obs-browserjs";

export default {
  name: "BasicMaterialSearch",
  components: {
    Plus,
    Picture,
    Edit,
  },
  setup() {
    const router = useRouter();

    const searchForm = reactive({
      materialNo: "",
    });

    const selectedSupplierId = ref("");
    const supplierOptions = ref([]);
    const supplierLoading = ref(false);

    const searchLoading = ref(false);
    const searchResults = ref([]);
    const searched = ref(false);

    const currentMaterial = ref(null);
    const imageChanged = ref(false);
    const originalImage = ref("");

    const uploadLoading = ref(false);
    const saveLoading = ref(false);
    const showImageModal = ref(false);

    const dialogTitle = computed(() => {
      if (!currentMaterial.value) return "上传图片";
      return originalImage.value || currentMaterial.value.image
        ? "修改图片"
        : "上传图片";
    });

    const OBS_CONFIG = {
      accessKeyId: "HPUAWIIOHK1UI8NIY6IV",
      secretAccessKey: "eG8ugo6F40yh03oDBtb88deVqZ57deJadDaM8r4S",
      bucketName: "wms-zwl",
      region: "cn-south-1",
      endpoint: "https://obs.cn-south-1.myhuaweicloud.com",
    };

    let obsClient = null;

    const initOBSClient = () => {
      if (obsClient) return;
      obsClient = new OBS({
        access_key_id: OBS_CONFIG.accessKeyId,
        secret_access_key: OBS_CONFIG.secretAccessKey,
        server: OBS_CONFIG.endpoint,
        signature: "obs",
      });
    };

    const formatImageUrl = (url) => {
      if (!url) return "";
      let cleanUrl = String(url).trim().replace(/`/g, "");
      cleanUrl = cleanUrl.replace(
        "wms-zwl.cn-south-1.myhuaweicloud.com",
        "wms-zwl.obs.cn-south-1.myhuaweicloud.com",
      );
      return cleanUrl;
    };

    // 从完整 URL 解析 OBS Object Key，仅处理本桶 material/ 下的图片
    const extractObsKey = (url) => {
      const cleaned = formatImageUrl(url);
      if (!cleaned) return "";
      try {
        const u = new URL(cleaned);
        const hostOk =
          u.hostname.includes(OBS_CONFIG.bucketName) &&
          u.hostname.includes("myhuaweicloud.com");
        if (!hostOk) return "";
        const key = decodeURIComponent(u.pathname.replace(/^\/+/, ""));
        if (!key.startsWith("material/")) return "";
        return key;
      } catch {
        return "";
      }
    };

    const deleteObsObject = (url) =>
      new Promise((resolve) => {
        const key = extractObsKey(url);
        if (!key) {
          resolve(false);
          return;
        }
        initOBSClient();
        obsClient.deleteObject(
          {
            Bucket: OBS_CONFIG.bucketName,
            Key: key,
          },
          (err, result) => {
            const status = result?.CommonMsg?.Status;
            if (err || (status && status >= 300)) {
              console.error("OBS删除失败:", err || result?.CommonMsg, key);
              resolve(false);
              return;
            }
            resolve(true);
          },
        );
      });

    const goBack = () => {
      router.push("/home");
    };

    const handleSupplierInput = async (keyword) => {
      if (!keyword) {
        supplierOptions.value = [];
        return;
      }
      supplierLoading.value = true;
      try {
        const res = await getSuppliers({ keyword, current: 1, size: 20 });
        const data = res?.data || {};
        supplierOptions.value = Array.isArray(data?.records)
          ? data.records
          : [];
      } catch (e) {
        console.error("搜索供应商失败:", e);
        supplierOptions.value = [];
      } finally {
        supplierLoading.value = false;
      }
    };

    const handleSearch = async () => {
      const materialNo = searchForm.materialNo.trim();
      if (!materialNo) {
        ElMessage.warning("请输入物料编码");
        return;
      }

      searchLoading.value = true;
      searched.value = true;

      try {
        const params = {
          materialNo,
          type: 1,
          current: 1,
          size: 20,
          isCompose: 0,
        };

        if (selectedSupplierId.value) {
          params.supplierId = selectedSupplierId.value;
        }

        const res = await searchMaterialByNo(params);
        const data = res?.data || {};
        searchResults.value = Array.isArray(data?.records) ? data.records : [];
      } catch (e) {
        console.error("搜索物料失败:", e);
        searchResults.value = [];
        ElMessage.error("搜索失败，请重试");
      } finally {
        searchLoading.value = false;
      }
    };

    const handleReset = () => {
      searchForm.materialNo = "";
      selectedSupplierId.value = "";
      supplierOptions.value = [];
      searchResults.value = [];
      searched.value = false;
    };

    const openImageModal = async (item) => {
      try {
        const res = await service.get("/uic-system/material/detail", {
          params: { id: item.id },
          _skipNoDataRedirect: true,
        });
        currentMaterial.value = res?.data || { id: item.id, ...item };
        originalImage.value = currentMaterial.value.image || "";
        imageChanged.value = false;
        showImageModal.value = true;
      } catch (e) {
        console.error("获取物料详情失败:", e);
        ElMessage.error("获取详情失败，请重试");
      }
    };

    const closeImageModal = () => {
      showImageModal.value = false;
    };

    const onDialogClosed = async () => {
      // 未保存就关掉：清掉本次新上传但未入库的 OBS 文件，避免垃圾图
      const pendingUrl = currentMaterial.value?.image || "";
      if (
        imageChanged.value &&
        pendingUrl &&
        pendingUrl !== originalImage.value
      ) {
        await deleteObsObject(pendingUrl);
      }
      currentMaterial.value = null;
      originalImage.value = "";
      imageChanged.value = false;
      uploadLoading.value = false;
    };

    // OBS Key 片段清洗，避免路径分隔符等特殊字符
    const sanitizePathSegment = (name) => {
      const cleaned = String(name || "")
        .trim()
        .replace(/[\\/:*?"<>|]+/g, "_")
        .replace(/\s+/g, "_")
        .replace(/_+/g, "_")
        .replace(/^_|_$/g, "");
      return cleaned || "unknown";
    };

    const resolveBizDept = () => {
      const userInfo = getUicAuthUserInfo() || {};
      return (
        currentMaterial.value?.bizDept ||
        userInfo.bizDept ||
        userInfo.deptId ||
        ""
      );
    };

    const beforeUpload = (file) => {
      const isImage = file.type.startsWith("image/");
      if (!isImage) {
        ElMessage.error("只能上传图片文件");
        return false;
      }
      const isLt5M = file.size / 1024 / 1024 < 5;
      if (!isLt5M) {
        ElMessage.error("图片大小不能超过 5MB");
        return false;
      }
      if (!resolveBizDept()) {
        ElMessage.error("未获取到项目组信息(bizDept)，无法上传");
        return false;
      }
      uploadLoading.value = true;
      initOBSClient();
      return true;
    };

    const handleOBSUpload = (options) => {
      const { file, onSuccess, onError } = options;
      const bizDept = sanitizePathSegment(resolveBizDept());
      if (!bizDept || bizDept === "unknown") {
        uploadLoading.value = false;
        const msg = "未获取到项目组信息(bizDept)，无法上传";
        onError(new Error(msg));
        ElMessage.error(msg);
        return;
      }

      const timestamp = Date.now();
      const ext = file.name.split(".").pop() || "png";
      const materialNo = sanitizePathSegment(
        currentMaterial.value?.materialNo || "material",
      );
      const supplierName = sanitizePathSegment(
        currentMaterial.value?.supplierName || "未知供应商",
      );
      // material/{bizDept}/供应商_物料编码_时间戳.ext
      // OBS 无真实文件夹，带此前缀上传即等价于创建目录
      const objectKey = `material/${bizDept}/${supplierName}_${materialNo}_${timestamp}.${ext}`;

      obsClient.putObject(
        {
          Bucket: OBS_CONFIG.bucketName,
          Key: objectKey,
          SourceFile: file,
          ContentType: file.type || "image/png",
        },
        (err, result) => {
          uploadLoading.value = false;
          const status = result?.CommonMsg?.Status;
          if (err || (status && status >= 300)) {
            const msg =
              result?.CommonMsg?.Message ||
              err?.message ||
              "图片上传失败，请重试";
            console.error("OBS上传失败:", err || result?.CommonMsg);
            onError(err || new Error(msg));
            ElMessage.error(msg);
            return;
          }
          const imageUrl = `https://${OBS_CONFIG.bucketName}.obs.${OBS_CONFIG.region}.myhuaweicloud.com/${objectKey}`;
          onSuccess({ success: true, data: { link: imageUrl } });
        },
      );
    };

    const handleUploadSuccess = (response) => {
      uploadLoading.value = false;
      if (response.success && response.data?.link) {
        if (currentMaterial.value) {
          currentMaterial.value.image = response.data.link;
        }
        imageChanged.value = true;
        ElMessage.success("图片上传成功，请点击保存");
      } else {
        ElMessage.error(response.msg || "图片上传失败");
      }
    };

    const handleUploadError = (error) => {
      uploadLoading.value = false;
      console.error("上传失败:", error);
    };

    const removeImage = async () => {
      const currentUrl = currentMaterial.value?.image || "";
      // 本次新上传、尚未入库的图：立刻删 OBS；原图等点保存后再删
      if (currentUrl && currentUrl !== originalImage.value) {
        await deleteObsObject(currentUrl);
      }
      currentMaterial.value.image = "";
      imageChanged.value = true;
      ElMessage.success("图片已删除，请点击保存");
    };

    const handleSave = async () => {
      if (!imageChanged.value) {
        ElMessage.info("没有需要保存的变更");
        return;
      }

      saveLoading.value = true;
      try {
        const savedImageUrl = currentMaterial.value.image || "";
        const oldImageUrl = originalImage.value || "";
        const submitData = { ...currentMaterial.value };
        const removeFields = [
          "current",
          "size",
          "materialList",
          "stockList",
          "deptIds",
          "type",
          "subWareId",
          "materialId",
          "statusName",
          "unpackNum",
          "virtualMaterialNum",
          "goodStockNum",
          "badStockNum",
          "groupId",
          "month",
          "inOutType",
          "wareOutNum",
          "isBad",
          "deptName",
          "storeId",
          "stockNum",
          "resourceType",
        ];
        removeFields.forEach((field) => delete submitData[field]);

        const res = await service.post(
          "/uic-system/material/submit",
          submitData,
        );

        if (res.success) {
          // 业务保存成功后，同步删除华为云上已替换/已移除的旧图
          if (oldImageUrl && oldImageUrl !== savedImageUrl) {
            const deleted = await deleteObsObject(oldImageUrl);
            if (!deleted) {
              console.warn("业务已保存，但华为云旧图删除失败:", oldImageUrl);
            }
          }
          ElMessage.success("保存成功");
          const index = searchResults.value.findIndex(
            (item) => item.id === currentMaterial.value.id,
          );
          if (index !== -1) {
            searchResults.value[index].image = savedImageUrl;
          }
          imageChanged.value = false;
          originalImage.value = savedImageUrl;
          showImageModal.value = false;
        } else {
          ElMessage.error(res.msg || "保存失败");
        }
      } catch (e) {
        console.error("保存失败:", e);
        ElMessage.error("保存失败，请重试");
      } finally {
        saveLoading.value = false;
      }
    };

    return {
      searchForm,
      selectedSupplierId,
      supplierOptions,
      supplierLoading,
      searchLoading,
      searchResults,
      searched,
      currentMaterial,
      imageChanged,
      uploadLoading,
      saveLoading,
      showImageModal,
      dialogTitle,
      goBack,
      handleSupplierInput,
      handleSearch,
      handleReset,
      openImageModal,
      closeImageModal,
      onDialogClosed,
      beforeUpload,
      handleOBSUpload,
      handleUploadSuccess,
      handleUploadError,
      formatImageUrl,
      removeImage,
      handleSave,
    };
  },
};
</script>

<style scoped>
.page-container {
  height: 100vh;
  height: 100dvh;
  background: #f0f2f5;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.header {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 16px;
  padding-top: calc(10px + env(safe-area-inset-top));
  background: #23b1c0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
  z-index: 100;
}

.back-btn {
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 6px;
  cursor: pointer;
  padding: 0;
}

.back-btn:hover {
  background: rgba(255, 255, 255, 0.3);
}

.icon-back {
  width: 18px;
  height: 18px;
  filter: brightness(0) invert(1);
}

.title {
  flex: 1;
  margin: 0;
  font-size: 17px;
  font-weight: 600;
  text-align: center;
  color: #fff;
}

.header-right {
  width: 36px;
}

.content {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  padding: 12px;
  padding-bottom: calc(12px + env(safe-area-inset-bottom));
  box-sizing: border-box;
  overflow: hidden;
}

.search-panel {
  flex-shrink: 0;
  background: #fff;
  padding: 14px 14px 2px;
  border-radius: 8px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
  margin-bottom: 12px;
}

.filter-form :deep(.el-form-item) {
  margin-bottom: 12px;
}

.filter-form :deep(.el-form-item__label) {
  color: #606266;
  font-weight: 500;
}

.form-item-actions :deep(.el-form-item__content) {
  display: flex;
  gap: 8px;
  margin-left: 0 !important;
}

.form-item-actions .el-button {
  flex: 1;
}

.result-panel {
  flex: 1;
  min-height: 0;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
  padding: 12px;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}

.results-header {
  margin-bottom: 12px;
}

.results-title {
  font-size: 14px;
  font-weight: 600;
  color: #303133;
}

.results-title em {
  font-style: normal;
  color: #409eff;
  margin: 0 2px;
}

.material-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.material-card {
  border: 1px solid #ebeef5;
  border-radius: 8px;
  padding: 12px;
  background: #fff;
  transition: border-color 0.2s;
}

.material-card:hover {
  border-color: #c6e2ff;
}

.card-main {
  display: flex;
  gap: 12px;
}

.card-image-wrap {
  flex: 0 0 88px;
  width: 88px;
  height: 88px;
  border-radius: 8px;
  overflow: hidden;
  background: #f5f7fa;
  flex-shrink: 0;
}

.card-image {
  width: 100%;
  height: 100%;
  display: block;
}

.card-image :deep(.el-image__inner) {
  width: 100%;
  height: 100%;
}

.card-image-empty {
  width: 100%;
  height: 100%;
  min-height: 88px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f5f7fa;
}

.empty-icon {
  font-size: 28px;
  color: #c0c4cc;
}

.card-info {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 6px;
}

.supplier-tag {
  align-self: flex-start;
  max-width: 100%;
}

.info-code {
  font-size: 15px;
  font-weight: 700;
  color: #23b1c0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  line-height: 1.3;
}

.info-desc {
  font-size: 12px;
  color: #606266;
  line-height: 1.45;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.card-footer {
  margin-top: 10px;
  padding-top: 8px;
  border-top: 1px dashed #ebeef5;
  display: flex;
  justify-content: flex-end;
}

.image-modal-content {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.modal-material-info {
  padding: 12px;
  background: #f5f7fa;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.modal-material-code {
  font-size: 15px;
  font-weight: 600;
  color: #23b1c0;
}

.modal-material-desc {
  font-size: 12px;
  color: #909399;
  line-height: 1.4;
}

.modal-section {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.section-label {
  font-size: 13px;
  color: #606266;
  font-weight: 500;
}

.modal-image-box {
  position: relative;
}

.modal-preview-image {
  width: 100%;
  height: 180px;
  border-radius: 8px;
  background: #f5f7fa;
  display: block;
}

.modal-preview-image :deep(.el-image__inner) {
  width: 100%;
  height: 180px;
}

.modal-image-empty {
  width: 100%;
  height: 180px;
  border-radius: 8px;
  background: #f5f7fa;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: #909399;
  font-size: 13px;
}

.modal-remove-btn {
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 2;
}

.modal-upload {
  width: 100%;
}

.modal-upload :deep(.el-upload),
.modal-upload :deep(.el-upload-dragger) {
  width: 100%;
}

.modal-upload :deep(.el-upload-dragger) {
  padding: 20px 12px;
}

.upload-icon {
  font-size: 28px;
  color: #23b1c0;
  margin-bottom: 6px;
}

.upload-text {
  font-size: 13px;
  color: #303133;
  margin-bottom: 4px;
}

.upload-tip {
  font-size: 12px;
  color: #909399;
}

@media (min-width: 768px) {
  .content {
    width: 100%;
    max-width: 720px;
    margin: 0 auto;
    padding: 16px 24px;
  }

  .material-list {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 12px;
  }
}
</style>
